import { useEffect } from 'react'
import { Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { useNavigate } from 'react-router-dom'
import {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  type Notification,
} from '@/redux/marketerApi'
import { useState } from 'react'
import { collabPath } from '@/lib/collabPath'

export const NotificationBell = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const { data, isLoading } = useGetNotificationsQuery(undefined, {
    pollingInterval: 180000,
  })

  const notifications = data?.notifications || []
  const unreadCount = data?.unreadCount || 0

  const [markRead] = useMarkNotificationReadMutation()
  const [markAllRead] = useMarkAllNotificationsReadMutation()

  const getNotificationUrl = (notification: Notification): string | null => {
    // Construct URL based on notification type and available data
    // Note: The route is /campaign/:campaignId/athlete/:collaborationId
    // despite the "athlete" in the path, the param is actually collaboration_id

    // Always use collaboration_id (from the base notification object, which is already hashed).
    // Standalone collaborations have no wave_id, so fall back to the campaign-free path.
    if (notification.collaboration_id) {
      return collabPath(notification.metadata?.wave_id, notification.collaboration_id)
    } else if (notification.metadata?.wave_id) {
      // If no collaboration_id, just go to the campaign detail page
      return `/campaign/${notification.metadata.wave_id}`
    }
    return null
  }

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      markRead(notification.id)
    }

    // Navigate to constructed URL
    const url = getNotificationUrl(notification)
    console.log(url)
    if (url) {
      navigate(url)
    }

    setOpen(false)
  }

  const handleMarkAllAsRead = () => {
    markAllRead()
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return `${Math.floor(diffMins / 1440)}d ago`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative hover:bg-foreground/10">
          <Bell className="h-6 w-6" style={{ color: '#3F7A0A' }} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-red-500 text-foreground text-xs font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 bg-card border-foreground/10 text-foreground"
        align="end"
      >
        <div className="flex items-center justify-between border-b border-foreground/10 p-3">
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs h-8 text-gray-600 hover:text-foreground hover:bg-foreground/10"
            >
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No notifications yet</div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  p-3 border-b border-foreground/10 cursor-pointer hover:bg-foreground/5 transition-colors
                  ${!notification.is_read ? 'bg-[#C0FD72]/5' : ''}
                `}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm text-foreground ${!notification.is_read ? 'font-semibold' : ''}`}
                      >
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <div className="h-2 w-2 rounded-full bg-[#C0FD72] mt-1 shrink-0" />
                      )}
                    </div>
                    {notification.body && (
                      <p className="text-xs text-gray-600 mt-1">{notification.body}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
